import DataLayerService from "../../../DataLayerService.js";
import isValidComponent from "../../common/isValidComponent.js";
import { handleDlEvent, hasDlEventAttribute, collectAttributes } from "../../common/dl-event-handler.js";
import { collectComponentAttributes } from "../../common/component-attributes.js";
import { getPromotionTracker } from "../../handlers/promotion-tracker-registry.js";
import { IGNORED_ATTRIBUTES, CLICK_CONTEXT_IGNORED_ATTRIBUTES } from "../../../config.js";

const datalayerService = new DataLayerService();

// Registriere Click Event Listener
document.addEventListener("click", handleClick);

/**
 * Haupt-Click-Handler
 * Erstellt Events basierend auf Attributen:
 * 1. dl-event Attribut → wird ignoriert (hat Vorrang, wird separat behandelt)
 * 2. product-id oder dl-product → select_item Event
 * 3. promotion-name/promotion-creative → select_promotion Event
 * 4. Fallback → click Event
 */
export function handleClick(event) {
	const { target } = event;

	// Prüfe ob Element in einer validen Komponente ist
	if (!isValidComponent(target)) {
		return;
	}

	// dl-event hat Vorrang - aber nur wenn es kein Event-Typ-spezifisches Event ist (z.B. "copy")
	if (hasDlEventAttribute(target)) {
		const dlEventElement = target.hasAttribute("dl-event")
			? target
			: target.closest("[dl-event]");
		const eventName = dlEventElement?.getAttribute("dl-event");

		// Überspringe Event-Typ-spezifische Events (copy, etc.)
		// Diese werden von ihren eigenen Handlern behandelt
		const eventTypeSpecificEvents = ["copy"];
		if (!eventTypeSpecificEvents.includes(eventName)) {
			return handleDlEvent(event);
		}
	}

	// 1. select_item Event
	if (isSelectItemClick(target)) {
		return trackSelectItem(target);
	}

	// 2. select_promotion Event
	if (isSelectPromotionClick(target)) {
		return trackSelectPromotion(target);
	}

	// 3. Fallback: click Event
	if (isClickableElement(target)) {
		return trackClick(target);
	}
}

/**
 * Prüft ob es ein select_item Click ist
 * Bedingung: Element hat product-id ODER dl-product Child
 * Ausnahme: Warenkorb-Buttons (basket="add") sollen kein select_item auslösen
 */
function isSelectItemClick(element) {
	// Warenkorb-Buttons ausschließen (basket="add")
	// basket="add" ist ein legacy Attribut. Diese Zeilen können entfernt werden,
	// wenn dieses Attribut ersetzt/entfernt wurde
	if (element.hasAttribute("basket") && element.getAttribute("basket") === "add") {
		return false;
	}
	if (element.closest("[basket='add']")) {
		return false;
	}

	// Hat product-id Attribut?
	if (element.hasAttribute("product-id") || element.closest("[product-id]")) {
		return true;
	}

	// Hat dl-product Child?
	if (element.querySelector("dl-product")) {
		return true;
	}

	return false;
}

/**
 * Prüft ob es ein select_promotion Click ist
 * Bedingung: Element oder Parent hat promotion-name oder promotion-creative
 */
function isSelectPromotionClick(element) {
	return !!(
		element.hasAttribute("promotion-name") ||
		element.hasAttribute("promotion-creative") ||
		element.closest("[promotion-name]") ||
		element.closest("[promotion-creative]")
	);
}

/**
 * Prüft ob Element klickbar ist (Link, Button, etc.)
 */
function isClickableElement(element) {
	const clickTarget = element.tagName === "A" ||
		element.tagName === "BUTTON" ||
		element.tagName === "SUMMARY" ||
		(element.tagName === "INPUT" && element.type === "submit")
		? element
		: element.closest("a, button, summary, input[type='submit']");

	return !!clickTarget;
}

/**
 * Erstellt select_item Event
 */
function trackSelectItem(target) {
	// Finde Produktelement (dl-product hat Vorrang)
	const productElement = getProductElement(target);
	if (!productElement) {
		return;
	}

	// Sammle Produktdaten
	const itemData = collectAttributes(productElement);

	// Sammle Event-Level Parameter (item_list_id, item_list_name)
	const listParams = getListParams(target);

	// Erstelle items Array
	const items = [itemData];

	// Pushe Event
	datalayerService.pushEvent("select_item", listParams, items);
}

/**
 * Findet das Produktelement (dl-product oder product-id)
 */
function getProductElement(target) {
	// 1. dl-product hat Vorrang
	const dlProduct = target.querySelector("dl-product");
	if (dlProduct) {
		return dlProduct;
	}

	// 2. Element mit product-id
	if (target.hasAttribute("product-id")) {
		return target;
	}

	const productIdElement = target.querySelector("[product-id]") || target.closest("[product-id]");
	return productIdElement;
}

/**
 * Erstellt select_promotion Event
 */
function trackSelectPromotion(target) {
	// Finde Promotion-Elemente
	const promotionNameElement = target.hasAttribute("promotion-name")
		? target
		: target.closest("[promotion-name]");

	const promotionCreativeElement = target.hasAttribute("promotion-creative")
		? target
		: target.closest("[promotion-creative]");

	if (!promotionNameElement && !promotionCreativeElement) {
		return;
	}

	// Sammle Event-Parameter
	const params = {};

	// component (wird für promotion_id Fallback benötigt)
	const componentElement = target.closest("[component]");
	const componentName = componentElement?.getAttribute("component");

	// promotion_id (von promotion-id Attribut, Fallback zu component)
	if (promotionCreativeElement?.hasAttribute("promotion-id")) {
		params.promotion_id = promotionCreativeElement.getAttribute("promotion-id");
	} else if (componentName) {
		params.promotion_id = componentName;
	}

	// promotion_name
	if (promotionNameElement) {
		params.promotion_name = promotionNameElement.getAttribute("promotion-name");
	}

	// creative_name (von promotion-creative)
	if (promotionCreativeElement) {
		params.creative_name = promotionCreativeElement.getAttribute("promotion-creative");
	}

	// creative_slot (Position als String)
	if (promotionNameElement && promotionCreativeElement) {
		const position = calculatePromotionPosition(promotionNameElement, promotionCreativeElement);
		params.creative_slot = String(position);
	}

	// Sammle zusätzliche Attribute vom component-Element
	const componentAttributes = collectComponentAttributes(target);
	Object.assign(params, componentAttributes);

	// Sicherstellen, dass view_promotion vor select_promotion ankommt —
	// sonst kann GA4 den Klick nicht der Impression zuordnen.
	ensureViewPromotionFired(promotionCreativeElement);

	// Pushe Event
	datalayerService.pushEvent("select_promotion", params);
}

/**
 * Löst ein view_promotion-Event nach, falls für dieses Promotion-Element
 * noch keines abgesetzt wurde (z.B. wenn der Klick erfolgt, bevor der
 * IntersectionObserver getriggert hat).
 */
function ensureViewPromotionFired(promotionCreativeElement) {
	if (!promotionCreativeElement) {
		return;
	}
	const tracker = getPromotionTracker();
	tracker?.ensurePromotionTracked(promotionCreativeElement);
}

/**
 * Berechnet die Position der Promotion in der Liste
 * (aus DataLayerPromotion.js übernommen)
 */
function calculatePromotionPosition(promotionElement, creativeElement) {
	const creativeElements = promotionElement.querySelectorAll("[promotion-creative]");
	const promotionCreative = creativeElement.getAttribute("promotion-creative");

	const index = Array.from(creativeElements).findIndex(element => {
		return element.getAttribute("promotion-creative") === promotionCreative;
	});

	return index < 0 ? 0 : (index + 1);
}

/**
 * Erstellt generisches click Event
 */
function trackClick(target) {
	// Finde klickbares Element
	const clickTarget = target.tagName === "A" ||
		target.tagName === "BUTTON" ||
		target.tagName === "SUMMARY" ||
		(target.tagName === "INPUT" && target.type === "submit")
		? target
		: target.closest("a, button, summary, input[type='submit']");

	if (!clickTarget) {
		return;
	}

	// Sammle Parameter
	const params = {};

	// component (required) - suche vom original target, nicht vom clickTarget
	const componentElement = target.closest("[component]");
	if (componentElement) {
		params.component = componentElement.getAttribute("component");
	}

	// list (optional von product-list) - suche vom original target
	const productListElement = target.closest("[product-list]");
	if (productListElement) {
		params.list = productListElement.getAttribute("product-list");
	}

	// interaction (Attribut oder innerText)
	params.interaction = getInteraction(clickTarget);

	// label (caption Attribut oder innerText)
	params.label = getLabel(clickTarget);

	// Alle weiteren Custom-Attribute vom Click-Target extrahieren
	const customAttributes = extractCustomAttributes(clickTarget);
	Object.assign(params, customAttributes);

	// Pushe Event
	datalayerService.pushEvent("click", params);
}

/**
 * Ermittelt interaction Wert
 * Priorität: interaction Attribut → innerText
 */
function getInteraction(element) {
	// 1. interaction Attribut am Element
	if (element.hasAttribute("interaction")) {
		return element.getAttribute("interaction");
	}

	// 2. interaction Attribut am Parent
	const parentWithInteraction = element.closest("[interaction]");
	if (parentWithInteraction) {
		return parentWithInteraction.getAttribute("interaction");
	}

	// 3. Spezial: <summary> Element
	if (element.tagName === "SUMMARY") {
		const detailsOpen = element.closest("details")?.open;
		return detailsOpen ? "details-summary-close" : "details-summary-open";
	}

	// 4. Fallback: innerText
	return element.textContent?.trim() || "";
}

/**
 * Ermittelt label Wert
 * Priorität: caption Attribut → innerText
 */
function getLabel(element) {
	const caption = element.getAttribute("caption");
	if (caption) {
		return caption;
	}
	return element.textContent?.trim() || "";
}

/**
 * Extrahiert item_list_id, item_list_name und zusätzliche Attribute vom component Element
 */
function getListParams(target) {
	const params = {};

	const componentElement = target.closest("[component]");
	if (componentElement) {
		const componentName = componentElement.getAttribute("component");
		if (componentName) {
			params["item-list-id"] = componentName;
		}

		const productList = componentElement.getAttribute("product-list");
		if (productList) {
			params["item-list-name"] = productList;
		}
	}

	// Sammle zusätzliche Attribute vom component-Element
	const componentAttributes = collectComponentAttributes(target);
	Object.assign(params, componentAttributes);

	return params;
}

/**
 * Extrahiert Custom-Attribute vom Element (filtert Standard-Browser-Attribute)
 * Wird für das click Event verwendet
 */
function extractCustomAttributes(element) {
	const attributes = {};

	if (!element || !element.attributes) {
		return attributes;
	}

	for (const attr of element.attributes) {
		const name = attr.name;
		const value = attr.value;

		// Leere Werte ignorieren
		if (!value) {
			continue;
		}

		// Browser-Standard-Attribute ignorieren
		if (IGNORED_ATTRIBUTES.includes(name)) {
			continue;
		}

		// Click-Kontext-Attribute ignorieren (werden bereits separat behandelt)
		if (CLICK_CONTEXT_IGNORED_ATTRIBUTES.includes(name)) {
			continue;
		}

		// Attributnamen transformieren: Hyphens → Underscores
		const transformedName = name.replace(/-/g, '_');
		attributes[transformedName] = value;
	}

	return attributes;
}
